"use client";

import {
	Disclosure,
	DisclosureButton,
	DisclosurePanel,
} from "@headlessui/react";
import { MinusSmallIcon, PlusSmallIcon } from "@heroicons/react/24/outline";
import { cn } from "../../../lib/cn";
import * as React from "react";

const ACCORDION_COLLAPSE_TRANSITION_MS = 300;

/**
 * Default path must stay `transition-[grid-template-rows]` only (pricing +
 * FeatureRow contract tests). Flag combos use full static Tailwind class names
 * so JIT can see them.
 */
function resolveCollapseTransitionClass(params: {
	fadeOpacity: boolean;
	animateMargin: boolean;
}): string {
	const { fadeOpacity, animateMargin } = params;
	if (fadeOpacity && animateMargin) {
		return "transition-[grid-template-rows,opacity,margin] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]";
	}
	if (fadeOpacity) {
		return "transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]";
	}
	if (animateMargin) {
		return "transition-[grid-template-rows,margin] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]";
	}
	// Shared default — pricing cubic-bezier (entity tiles adopt this in P0).
	return "transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]";
}

export interface AccordionCollapsePanelProps {
	open: boolean;
	children: React.ReactNode;
	className?: string;
	innerClassName?: string;
	/** Mount children only while open or during the collapse animation. Default false. */
	lazyMount?: boolean;
	/** When true, transition includes opacity 0↔100. Default false. */
	fadeOpacity?: boolean;
	/** When true, transition includes margin (values via className). Default false. */
	animateMargin?: boolean;
	/**
	 * When `"visible"` and open, allow overflow (e.g. quote marks). Default `"hidden"`.
	 */
	openOverflow?: "hidden" | "visible";
}

/**
 * Height-animated collapse that keeps children mounted when closed
 * (`grid-rows` 0fr → 1fr). Shared by AccordionDisclosure, FeatureRow, entity
 * structure tiles, and later L0 consumers.
 *
 * Inner `min-h-min` wrapper ensures the open `1fr` track remeasures when
 * wrapped/multiline content grows (without needing a hover reflow).
 */
export function AccordionCollapsePanel({
	open,
	children,
	className,
	innerClassName,
	lazyMount = false,
	fadeOpacity = false,
	animateMargin = false,
	openOverflow = "hidden",
}: AccordionCollapsePanelProps) {
	const [shouldRenderChildren, setShouldRenderChildren] = React.useState(
		() => !lazyMount || open,
	);
	const [rowsExpanded, setRowsExpanded] = React.useState(open);

	React.useEffect(() => {
		if (!lazyMount) {
			setRowsExpanded(open);
			setShouldRenderChildren(true);
			return;
		}

		if (open) {
			setShouldRenderChildren(true);
			setRowsExpanded(false);
			let raf2 = 0;
			const raf1 = requestAnimationFrame(() => {
				raf2 = requestAnimationFrame(() => {
					setRowsExpanded(true);
				});
			});
			return () => {
				cancelAnimationFrame(raf1);
				cancelAnimationFrame(raf2);
			};
		}

		setRowsExpanded(false);
		const timer = window.setTimeout(() => {
			setShouldRenderChildren(false);
		}, ACCORDION_COLLAPSE_TRANSITION_MS);
		return () => window.clearTimeout(timer);
	}, [open, lazyMount]);

	if (lazyMount && !shouldRenderChildren) {
		return null;
	}

	const expanded = lazyMount ? rowsExpanded : open;
	const allowOpenOverflow = expanded && openOverflow === "visible";

	return (
		<div
			className={cn(
				"grid",
				resolveCollapseTransitionClass({ fadeOpacity, animateMargin }),
				expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
				fadeOpacity
					? expanded
						? "opacity-100"
						: "opacity-0"
					: undefined,
				allowOpenOverflow ? "overflow-visible" : "overflow-hidden",
				"motion-reduce:transition-none",
				className,
			)}
		>
			<div
				className={cn(
					"min-h-0 min-w-0",
					allowOpenOverflow ? "overflow-visible" : "overflow-hidden",
					innerClassName,
				)}
			>
				<div className="min-h-min min-w-0">{children}</div>
			</div>
		</div>
	);
}

export interface AccordionDisclosureProps {
	/** Title / label shown on the left inside the clickable trigger (chevron on the right). */
	header: React.ReactNode;
	/** Rendered outside the disclosure button so toggles do not expand/collapse the panel. */
	headerEnd?: React.ReactNode;
	children: React.ReactNode;
	defaultOpen?: boolean;
	/**
	 * Controlled open. When set, Headless Disclosure is bypassed and L0 is
	 * driven directly (FAQ / L1 wiring). Uncontrolled path keeps Headless.
	 */
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	className?: string;
	triggerClassName?: string;
	/** Merged onto the trigger only while the disclosure is open. */
	openTriggerClassName?: string;
	panelClassName?: string;
}

const DISCLOSURE_TRIGGER_BASE_CLASS =
	"flex cursor-pointer items-center justify-between py-6 text-left text-gray-900 outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:text-white dark:focus-visible:ring-offset-[color:var(--marketing-surface)]";

function DisclosureExpandIcons({ open }: { open: boolean }) {
	return (
		<span className="relative ml-6 flex h-7 w-6 shrink-0 items-center justify-center self-center">
			<PlusSmallIcon
				aria-hidden="true"
				className={cn(
					"absolute inset-0 m-auto size-6 transition-opacity duration-300 ease-out",
					open ? "pointer-events-none opacity-0" : "opacity-100",
				)}
			/>
			<MinusSmallIcon
				aria-hidden="true"
				className={cn(
					"absolute inset-0 m-auto size-6 transition-opacity duration-300 ease-out",
					open ? "opacity-100" : "pointer-events-none opacity-0",
				)}
			/>
		</span>
	);
}

function TrackOpenChange({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange?: (open: boolean) => void;
}) {
	const prevOpenRef = React.useRef<boolean | null>(null);

	React.useEffect(() => {
		if (prevOpenRef.current === null) {
			prevOpenRef.current = open;
			return;
		}
		if (prevOpenRef.current !== open) {
			onOpenChange?.(open);
			prevOpenRef.current = open;
		}
	}, [open, onOpenChange]);

	return null;
}

function AccordionDisclosureChrome({
	open,
	header,
	headerEnd,
	children,
	onToggle,
	triggerClassName,
	openTriggerClassName,
	panelClassName,
	asDisclosureButton,
}: {
	open: boolean;
	header: React.ReactNode;
	headerEnd?: React.ReactNode;
	children: React.ReactNode;
	onToggle?: () => void;
	triggerClassName?: string;
	openTriggerClassName?: string;
	panelClassName?: string;
	/** When true, use Headless DisclosureButton (uncontrolled path). */
	asDisclosureButton: boolean;
}) {
	const triggerClass = cn(
		DISCLOSURE_TRIGGER_BASE_CLASS,
		headerEnd ? "min-w-0 flex-1" : "w-full",
		triggerClassName,
		open && openTriggerClassName,
	);

	const triggerInner = (
		<>
			<div className="flex min-w-0 flex-1 items-center">{header}</div>
			<DisclosureExpandIcons open={open} />
		</>
	);

	const trigger = asDisclosureButton ? (
		<DisclosureButton className={triggerClass} type="button">
			{triggerInner}
		</DisclosureButton>
	) : (
		<button
			type="button"
			aria-expanded={open}
			className={triggerClass}
			onClick={onToggle}
		>
			{triggerInner}
		</button>
	);

	return (
		<>
			<dt>
				{headerEnd ? (
					<div className="flex w-full items-stretch gap-3">
						{trigger}
						<div className="flex shrink-0 items-center">
							{headerEnd}
						</div>
					</div>
				) : (
					trigger
				)}
			</dt>
			{asDisclosureButton ? (
				<DisclosurePanel
					as="dd"
					className={cn("w-full", panelClassName)}
					static
					unmount={false}
				>
					<AccordionCollapsePanel open={open}>
						{children}
					</AccordionCollapsePanel>
				</DisclosurePanel>
			) : (
				<dd className={cn("w-full", panelClassName)}>
					<AccordionCollapsePanel open={open}>
						{children}
					</AccordionCollapsePanel>
				</dd>
			)}
		</>
	);
}

export function AccordionDisclosure({
	header,
	headerEnd,
	children,
	defaultOpen = false,
	open: openControlled,
	onOpenChange,
	className,
	triggerClassName,
	openTriggerClassName,
	panelClassName,
}: AccordionDisclosureProps) {
	const isControlled = openControlled !== undefined;

	if (isControlled) {
		return (
			<div className={className}>
				<AccordionDisclosureChrome
					open={openControlled}
					header={header}
					headerEnd={headerEnd}
					onToggle={() => onOpenChange?.(!openControlled)}
					triggerClassName={triggerClassName}
					openTriggerClassName={openTriggerClassName}
					panelClassName={panelClassName}
					asDisclosureButton={false}
				>
					{children}
				</AccordionDisclosureChrome>
			</div>
		);
	}

	return (
		<Disclosure as="div" className={className} defaultOpen={defaultOpen}>
			{({ open }) => (
				<>
					<TrackOpenChange open={open} onOpenChange={onOpenChange} />
					<AccordionDisclosureChrome
						open={open}
						header={header}
						headerEnd={headerEnd}
						triggerClassName={triggerClassName}
						openTriggerClassName={openTriggerClassName}
						panelClassName={panelClassName}
						asDisclosureButton
					>
						{children}
					</AccordionDisclosureChrome>
				</>
			)}
		</Disclosure>
	);
}
